import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Save, Eye } from "lucide-react";
import { useAdmin } from "./AdminApp";
import { PageTitle } from "./Layout";
import { Field, ImageUpload } from "../../components/admin/Fields";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DateTimePicker } from "../../components/ui/datetime-picker";
import PromoModal from "../site/PromoModal";
import { errMsg } from "../../lib/api";

const DEFAULTS = {
  enabled: false,
  media_type: "image",
  image_url: "",
  image_alt: "",
  image_link: "",
  youtube_url: "",
  video_url: "",
  autoplay: true,
  muted: true,
  loop: false,
  headline: "",
  description: "",
  cta_enabled: false,
  cta_text: "Devamı",
  cta_url: "",
  cta_color: "#14213D",
  cta_text_color: "#FFFFFF",
  cta_align: "left",
  buttons: [],
  panel_bg_color: "#FFFFFF",
  panel_text_color: "#1C1917",
  position: "center",
  size: "md",
  delay_seconds: 1,
  frequency: "session",
  frequency_days: 7,
  show_dont_show_again: true,
  start_at: "",
  end_at: "",
};

export default function PromoAdmin() {
  const { settings, saveSection } = useAdmin();
  const [promo, setPromo] = useState({ ...DEFAULTS, ...(settings?.promo || {}) });
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    setPromo({ ...DEFAULTS, ...(settings?.promo || {}) });
  }, [settings?.promo]);

  const update = (patch) => setPromo((p) => ({ ...p, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      await saveSection("promo", promo);
      toast.success("Açılış reklamı kaydedildi");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const preview = () => setPreviewing(true);

  return (
    <>
      <PageTitle
        title="Açılış Reklamı"
        sub="Site açılışında modal olarak gösterilecek görsel/video/YouTube reklamını buradan yönetin."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={preview} data-testid="promo-preview">
              <Eye size={14} className="mr-1" /> Önizle
            </Button>
            <Button onClick={save} disabled={busy} data-testid="promo-save">
              <Save size={14} className="mr-1" /> {busy ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader icon={<Megaphone size={18} className="text-amber-500" />} title="Durum ve medya" />
            <div className="p-5 space-y-5">
              <label className="flex items-center justify-between gap-4 bg-stone-50 border rounded-lg px-4 py-3">
                <div>
                  <p className="font-medium text-sm">Reklam yayında</p>
                  <p className="text-xs text-stone-500 mt-0.5">Kapatırsanız modal hiç gösterilmez.</p>
                </div>
                <Switch checked={promo.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="promo-enabled" />
              </label>

              <Field label="Medya türü">
                <div className="grid grid-cols-3 gap-2">
                  {[["image", "Görsel"], ["youtube", "YouTube"], ["video", "MP4 Video"]].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => update({ media_type: k })}
                      className={`py-2 rounded-md text-sm border transition ${promo.media_type === k ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:bg-stone-50"}`}
                      data-testid={`promo-media-${k}`}
                    >{l}</button>
                  ))}
                </div>
              </Field>

              {promo.media_type === "image" && (
                <>
                  <ImageUpload
                    label="Görsel"
                    value={promo.image_url}
                    onChange={(url) => update({ image_url: url })}
                    testid="promo-image"
                    alt={promo.image_alt}
                  />
                  <Field label="Görsel alt metni (SEO/erişilebilirlik)">
                    <Input value={promo.image_alt} onChange={(e) => update({ image_alt: e.target.value })} data-testid="promo-image-alt" />
                  </Field>
                  <Field label="Tıklanınca gidilecek bağlantı (opsiyonel)" hint="Boş bırakırsanız görsel tıklanabilir olmaz.">
                    <Input value={promo.image_link} onChange={(e) => update({ image_link: e.target.value })} placeholder="https://…" data-testid="promo-image-link" />
                  </Field>
                </>
              )}

              {promo.media_type === "youtube" && (
                <Field label="YouTube bağlantısı" hint="Örn. https://youtu.be/xxxxx veya https://www.youtube.com/watch?v=xxxxx">
                  <Input value={promo.youtube_url} onChange={(e) => update({ youtube_url: e.target.value })} placeholder="https://youtu.be/…" data-testid="promo-youtube" />
                </Field>
              )}

              {promo.media_type === "video" && (
                <>
                  <Field label="MP4 video bağlantısı" hint="Doğrudan .mp4 uzantılı bir URL verin.">
                    <Input value={promo.video_url} onChange={(e) => update({ video_url: e.target.value })} placeholder="https://…/reklam.mp4" data-testid="promo-video-url" />
                  </Field>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <Switch checked={promo.autoplay} onCheckedChange={(v) => update({ autoplay: v })} data-testid="promo-autoplay" />
                      Otomatik oynat
                    </label>
                    <label className="flex items-center gap-2">
                      <Switch checked={promo.muted} onCheckedChange={(v) => update({ muted: v })} data-testid="promo-muted" />
                      Sessiz
                    </label>
                    <label className="flex items-center gap-2">
                      <Switch checked={promo.loop} onCheckedChange={(v) => update({ loop: v })} data-testid="promo-loop" />
                      Döngü
                    </label>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="İçerik" />
            <div className="p-5 space-y-5">
              <Field label="Başlık (opsiyonel)">
                <Input value={promo.headline} onChange={(e) => update({ headline: e.target.value })} placeholder="Örn. Yeni kampanya başladı!" data-testid="promo-headline" />
              </Field>
              <Field label="Açıklama (opsiyonel)">
                <Textarea rows={3} value={promo.description} onChange={(e) => update({ description: e.target.value })} data-testid="promo-description" />
              </Field>

              <label className="flex items-center justify-between gap-4 bg-stone-50 border rounded-lg px-4 py-3">
                <div>
                  <p className="font-medium text-sm">Buton göster</p>
                  <p className="text-xs text-stone-500 mt-0.5">1 veya 2 buton yan yana. Her butonu ayrı ayrı açıp kapatabilirsin.</p>
                </div>
                <Switch checked={promo.cta_enabled} onCheckedChange={(v) => update({ cta_enabled: v })} data-testid="promo-cta-enabled" />
              </label>

              {promo.cta_enabled && (
                <div className="space-y-4">
                  <Field label="Buton hizası">
                    <div className="grid grid-cols-3 gap-2">
                      {[["left", "Sol"], ["center", "Orta"], ["right", "Sağ"]].map(([k, l]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => update({ cta_align: k })}
                          className={`py-2 rounded-md text-sm border transition ${promo.cta_align === k ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:bg-stone-50"}`}
                          data-testid={`promo-cta-align-${k}`}
                        >{l}</button>
                      ))}
                    </div>
                  </Field>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[0, 1].map((i) => {
                      const btn = (promo.buttons && promo.buttons[i]) || { enabled: false, text: i === 0 ? "Devamı" : "", url: "", color: "#14213D", text_color: "#FFFFFF" };
                      const setBtn = (patch) => {
                        const next = [promo.buttons?.[0] || { enabled: false, text: "Devamı", url: "", color: "#14213D", text_color: "#FFFFFF" },
                                      promo.buttons?.[1] || { enabled: false, text: "", url: "", color: "#DC2626", text_color: "#FFFFFF" }];
                        next[i] = { ...next[i], ...patch };
                        update({ buttons: next });
                      };
                      return (
                        <div key={i} className="border rounded-lg p-4 space-y-3 bg-white">
                          <label className="flex items-center justify-between gap-3">
                            <span className="font-medium text-sm">Buton {i + 1}</span>
                            <Switch checked={!!btn.enabled} onCheckedChange={(v) => setBtn({ enabled: v })} data-testid={`promo-btn-${i}-enabled`} />
                          </label>
                          <Field label="Metin">
                            <Input value={btn.text} onChange={(e) => setBtn({ text: e.target.value })} data-testid={`promo-btn-${i}-text`} />
                          </Field>
                          <Field label="Bağlantı">
                            <Input value={btn.url} onChange={(e) => setBtn({ url: e.target.value })} placeholder="https://…" data-testid={`promo-btn-${i}-url`} />
                          </Field>
                          <div className="grid grid-cols-2 gap-3">
                            <Field label="Arka plan">
                              <input type="color" value={btn.color} onChange={(e) => setBtn({ color: e.target.value })}
                                className="w-full h-10 rounded border cursor-pointer" data-testid={`promo-btn-${i}-color`} />
                            </Field>
                            <Field label="Yazı rengi">
                              <input type="color" value={btn.text_color} onChange={(e) => setBtn({ text_color: e.target.value })}
                                className="w-full h-10 rounded border cursor-pointer" data-testid={`promo-btn-${i}-text-color`} />
                            </Field>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Panel renkleri" />
            <div className="p-5 space-y-4">
              <p className="text-xs text-stone-500">Modalda görselin altındaki yazı/buton alanının arka plan ve yazı rengini özelleştirir.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Panel arka plan rengi">
                  <input type="color" value={promo.panel_bg_color} onChange={(e) => update({ panel_bg_color: e.target.value })}
                    className="w-full h-10 rounded border cursor-pointer" data-testid="promo-panel-bg-color" />
                </Field>
                <Field label="Panel yazı rengi">
                  <input type="color" value={promo.panel_text_color} onChange={(e) => update({ panel_text_color: e.target.value })}
                    className="w-full h-10 rounded border cursor-pointer" data-testid="promo-panel-text-color" />
                </Field>
              </div>
            </div>
          </Card>
        </div>

        {/* SIDE */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="Konum ve boyut" />
            <div className="p-5 space-y-4">
              <Field label="Konum">
                <Select value={promo.position} onValueChange={(v) => update({ position: v })}>
                  <SelectTrigger data-testid="promo-position"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="center">Ekran ortası</SelectItem>
                    <SelectItem value="bottom-right">Sağ alt köşe</SelectItem>
                    <SelectItem value="bottom-left">Sol alt köşe</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Boyut">
                <Select value={promo.size} onValueChange={(v) => update({ size: v })}>
                  <SelectTrigger data-testid="promo-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Küçük</SelectItem>
                    <SelectItem value="md">Orta</SelectItem>
                    <SelectItem value="lg">Büyük</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Card>

          <Card>
            <CardHeader title="Zamanlama" />
            <div className="p-5 space-y-4">
              <Field label="Gösterim gecikmesi (saniye)" hint="Sayfa açıldıktan ne kadar sonra görünsün.">
                <Input type="number" min={0} max={60} value={promo.delay_seconds}
                  onChange={(e) => update({ delay_seconds: Number(e.target.value) })} data-testid="promo-delay" />
              </Field>
              <Field label="Gösterim sıklığı">
                <Select value={promo.frequency} onValueChange={(v) => update({ frequency: v })}>
                  <SelectTrigger data-testid="promo-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Her sayfa açılışında</SelectItem>
                    <SelectItem value="session">Oturum başına 1 kez</SelectItem>
                    <SelectItem value="days">Belirli gün aralığında 1 kez</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {promo.frequency === "days" && (
                <Field label="Kaç günde bir?">
                  <Input type="number" min={1} max={365} value={promo.frequency_days}
                    onChange={(e) => update({ frequency_days: Number(e.target.value) })} data-testid="promo-frequency-days" />
                </Field>
              )}
              <label className="flex items-center gap-2 text-sm pt-1">
                <Switch checked={promo.show_dont_show_again} onCheckedChange={(v) => update({ show_dont_show_again: v })} data-testid="promo-dsa" />
                "Bir daha gösterme" seçeneğini göster
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Başlangıç (opsiyonel)">
                  <DateTimePicker value={promo.start_at} onChange={(iso) => update({ start_at: iso })} testId="promo-start-at" />
                </Field>
                <Field label="Bitiş (opsiyonel)">
                  <DateTimePicker value={promo.end_at} onChange={(iso) => update({ end_at: iso })} testId="promo-end-at" />
                </Field>
              </div>
              <p className="text-xs text-stone-500">Her kayıtta kullanıcıların "bir daha gösterme" seçimleri sıfırlanır, modal yeniden görünür.</p>
            </div>
          </Card>
        </div>
      </div>

      {previewing && (
        <PromoModal
          promo={{ ...promo, version: `preview-${Date.now()}` }}
          forcePreview
          onClose={() => setPreviewing(false)}
        />
      )}
    </>
  );
}

function Card({ children }) {
  return <section className="bg-white border rounded-xl overflow-hidden">{children}</section>;
}
function CardHeader({ icon, title }) {
  return (
    <header className="px-5 py-3 border-b bg-stone-50 flex items-center gap-2">
      {icon}<h2 className="font-semibold text-sm">{title}</h2>
    </header>
  );
}
